import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import LoadFailure from "../../components/ui/LoadFailure";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import { downloadPathwayExport } from "../../pathways/adminApi";
import { pathwayErrorCode, pathwayErrorMessage } from "../../pathways/errors";
import usePathwayDashboard from "../../pathways/usePathwayDashboard";
import { useEntitlements } from "../entitlements/EntitlementsContext";
import { CAP } from "../entitlements/capabilities";
import { adminHref } from "../adminPath";
import ActionMenu from "./ActionMenu";
import AttendancePanel from "./AttendancePanel";
import EncourageModal from "./EncourageModal";
import FunnelChart from "./FunnelChart";
import PromptsPanel from "./PromptsPanel";
import ReviewQueue from "./ReviewQueue";
import saveBlob from "./download";
import { fmtDaysAgo } from "./format";
import { statusLabel, typeLabel } from "./labels";
import { pathwayPaths } from "./paths";
import { STATUS_PILL, TYPE_BADGE } from "./statusPill";

const TOTALS = Object.freeze(["enrolled", "active_this_week", "completed", "average_progress", "stuck"]);

/**
 * `/admin/pathways/:pathwayId`: the tracking dashboard of one pathway (spec screen 5,
 * contract §5.1 `PathwayDashboard`), in the wireframe's order:
 *
 *   title                                                     Export   ⋮ Edit / Duplicate / Archive
 *   [Enrolled] [Active this week] [Completed]
 *   [Average progress] [Stuck (14+ days)]         the stuck card is tinted
 *   Step funnel                                   the largest drop is highlighted
 *   (All learners) (Stuck · N)                                 [Send encouragement]
 *   NAME · CURRENT STEP · PROGRESS · LAST ACTIVE · QUIZ AVG    a row opens the CRM record
 *   Review queue · N                              practice submissions awaiting review
 *   Attendance · Group session · Thu 12 Nov       one per cohort live step
 *   Weekly prompts                                cohort pathways only
 *
 * The review queue and the attendance sections follow contract §10.11 ("between the
 * learners and the prompts"). The queue shows when the pathway asks for submissions on
 * some practice step, or still has submissions pending from a step that no longer does;
 * the attendance sections show for a cohort pathway's live steps (`live_sessions`).
 *
 * THE FIGURES ARE THE SERVER'S. Every count, percent and the `stuck` flag come from the
 * dashboard answer (computed at request time, contract §5.1); nothing is recomputed here,
 * so the Stuck chip, the table filter and the encouragement recipients cannot disagree.
 *
 * A ROW OPENS THE LEARNER'S CRM RECORD on its Pathways tab at this pathway
 * (`/admin/crm?contact=<email>&tab=pathways&pathway=<id>`).
 */
export default function PathwayDashboard() {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { pathwayId } = useParams();
  const d = usePathwayDashboard(pathwayId);
  const ent = useEntitlements();
  const coursesState = ent.status(CAP.courses);
  const canAuthor = coursesState !== "not_in_plan" && coursesState !== "coming_soon";
  const [filter, setFilter] = useState("all");
  const [encouraging, setEncouraging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { dashboard, pathway, reviews } = d;

  const stuck = useMemo(() => (dashboard ? dashboard.learners.filter((l) => l.stuck) : []), [dashboard]);
  const rows = filter === "stuck" ? stuck : (dashboard?.learners || []);

  if (d.loading && !dashboard) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-eh-muted" aria-label={t("path.loading")} /></div>;
  }
  if (d.error || !dashboard || !pathway || !reviews) {
    const missing = pathwayErrorCode(d.error) === "PATHWAY_NOT_FOUND" || d.error?.response?.status === 404;
    return (
      <div data-testid={IDS.dashboard} className="space-y-4">
        <BackLink />
        <LoadFailure
          message={missing ? t("path.notFound") : tc("errors.loadFailed")}
          onRetry={d.reload}
          retryLabel={t("path.retry")}
          testId={IDS.dashboardLoadError}
        />
      </div>
    );
  }

  const fail = (e) => toast.error(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));

  const onExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await downloadPathwayExport(pathwayId, lang);
      saveBlob(blob, filename || `pathway-${pathwayId}.csv`);
    } catch (e) {
      toast.error(pathwayErrorMessage(e, tc, t("path.dash.exportFailed")));
    } finally {
      setExporting(false);
    }
  };

  const onDuplicate = async () => {
    try {
      const copy = await d.duplicate(lang);
      toast.success(t("path.duplicated"));
      navigate(adminHref(pathwayPaths.edit(copy.id)));
    } catch (e) { fail(e); }
  };

  const onArchive = async () => {
    if (!window.confirm(t("path.confirmArchive", { title: dashboard.title }))) return;
    try {
      await d.archive();
      toast.success(t("path.archived"));
    } catch (e) { fail(e); }
  };

  const onSent = ({ sent, skipped }) => {
    setEncouraging(false);
    toast.success(skipped > 0 ? t("path.dash.encourageSentSkipped", { sent, skipped }) : t(sent === 1 ? "path.dash.encourageSentOne" : "path.dash.encourageSent", { n: sent }));
  };

  const totals = dashboard.totals;
  const totalValue = (name) => {
    if (name === "average_progress") return totals.average_progress == null ? "—" : `${totals.average_progress}%`;
    return String(totals[name]);
  };

  return (
    <div data-testid={IDS.dashboard} className="space-y-5">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 data-testid={IDS.dashboardTitle} className="font-display text-3xl text-eh-ink leading-tight break-words">{dashboard.title}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <span className={`text-[11px] px-2.5 py-0.5 rounded-full ${TYPE_BADGE}`}>{typeLabel(t, dashboard.path_type)}</span>
            <span className={`text-[11px] px-2.5 py-0.5 rounded-full ${STATUS_PILL[dashboard.status] || STATUS_PILL.draft}`}>
              {statusLabel(t, dashboard.status)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onExport} disabled={exporting} data-testid={IDS.exportCsv} className="text-eh-ochre-deep font-semibold">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4 mr-2" aria-hidden="true" />}
            {t("path.dash.export")}
          </Button>
          <ActionMenu
            label={t("path.menu.label", { title: dashboard.title })}
            testId={IDS.dashboardMenu}
            items={[
              { key: "edit", label: t("path.menu.edit"), onSelect: () => navigate(adminHref(pathwayPaths.edit(pathwayId))), testId: IDS.edit },
              { key: "duplicate", label: t("path.menu.duplicate"), onSelect: onDuplicate, disabled: !canAuthor, testId: IDS.duplicate },
              { key: "archive", label: t("path.menu.archive"), onSelect: onArchive, disabled: dashboard.status === "archived", danger: true, testId: IDS.archive },
            ]}
          />
        </div>
      </header>

      <section aria-label={t("path.dash.summary")} className="grid gap-3 grid-cols-2 md:grid-cols-3">
        {TOTALS.map((name) => (
          <div
            key={name}
            data-testid={IDS.total(name)}
            className={`rounded-2xl border px-4 py-3 ${name === "stuck" ? "bg-rose-50 border-rose-200" : "bg-white border-eh-border-soft"}`}
          >
            <p className="text-sm text-eh-muted">{t(`path.dash.total.${name}`)}</p>
            <p className="text-3xl font-semibold text-eh-ink tabular-nums mt-1">{totalValue(name)}</p>
          </div>
        ))}
      </section>

      <section aria-labelledby="pathway-funnel-heading" className="bg-white rounded-3xl border border-eh-border-soft p-5">
        <h2 id="pathway-funnel-heading" className="text-sm font-semibold text-eh-ink mb-3">{t("path.dash.funnel")}</h2>
        {dashboard.funnel.length > 1
          ? <FunnelChart funnel={dashboard.funnel} />
          : <p className="text-sm text-eh-muted">{t("path.dash.funnelEmpty")}</p>}
      </section>

      <section aria-labelledby="pathway-learners-heading" className="bg-white rounded-3xl border border-eh-border-soft p-5 space-y-4">
        <h2 id="pathway-learners-heading" className="sr-only">{t("path.dash.learners")}</h2>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="group" aria-label={t("path.dash.filter")} className="flex gap-2">
            <Chip active={filter === "all"} onClick={() => setFilter("all")} testId={IDS.filterAll}>{t("path.dash.filterAll")}</Chip>
            <Chip active={filter === "stuck"} onClick={() => setFilter("stuck")} testId={IDS.filterStuck}>
              {t("path.dash.filterStuck", { n: totals.stuck })}
            </Chip>
          </div>
          <Button
            onClick={() => setEncouraging(true)}
            disabled={stuck.length === 0 || dashboard.status === "archived"}
            data-testid={IDS.encourage}
            className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90"
          >
            <Send className="w-4 h-4 mr-2" aria-hidden="true" /> {t("path.dash.encourage")}
          </Button>
        </div>
        <LearnersTable rows={rows} pathwayId={pathwayId} lang={lang} />
      </section>

      {showsReviewQueue(pathway, dashboard) && (
        <ReviewQueue
          pending={dashboard.review_pending}
          items={reviews}
          onApprove={d.approve}
          onFeedback={d.feedback}
          onStale={d.reload}
        />
      )}

      {pathway.schedule === "cohort" && dashboard.live_sessions.map((session) => (
        <AttendancePanel
          // A re-read with new attendance starts the boxes again from the server's.
          key={`${session.step_id}:${[...session.attended].sort().join(",")}`}
          session={session}
          learners={dashboard.learners}
          onSave={d.saveAttendance}
          disabled={dashboard.status === "archived"}
        />
      ))}

      {pathway.schedule === "cohort" && (
        <PromptsPanel pathway={pathway} onPost={d.prompt} disabled={pathway.status === "archived"} />
      )}

      <EncourageModal
        open={encouraging}
        recipients={stuck}
        onSend={d.encourage}
        onSent={onSent}
        onClose={() => setEncouraging(false)}
      />
    </div>
  );
}

/** Whether the dashboard has a review queue to show (see the component docstring). */
function showsReviewQueue(pathway, dashboard) {
  if (dashboard.review_pending > 0) return true;
  return pathway.levels.some((l) => l.steps.some((s) => s.kind === "practice" && s.practice?.submission_required));
}

function LearnersTable({ rows, pathwayId, lang }) {
  const t = useT("admin");
  const navigate = useNavigate();
  if (rows.length === 0) {
    return <p data-testid={IDS.learnersEmpty} className="text-sm text-eh-muted py-4">{t("path.dash.noLearners")}</p>;
  }
  const head = "text-left text-[11px] font-semibold uppercase tracking-wider text-eh-muted px-3 py-2";
  return (
    <div className="overflow-x-auto">
      <table data-testid={IDS.learners} className="w-full text-sm">
        <thead className="border-b border-eh-border-soft">
          <tr>
            <th scope="col" className={head}>{t("path.dash.col.name")}</th>
            <th scope="col" className={head}>{t("path.dash.col.step")}</th>
            <th scope="col" className={head}>{t("path.dash.col.progress")}</th>
            <th scope="col" className={head}>{t("path.dash.col.lastActive")}</th>
            <th scope="col" className={head}>{t("path.dash.col.quiz")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-eh-border-soft">
          {rows.map((l) => {
            const href = adminHref(pathwayPaths.crmContact(l.email, pathwayId));
            return (
              <tr
                key={l.email}
                data-testid={IDS.learnerRow(l.email)}
                onClick={() => navigate(href)}
                className="cursor-pointer hover:bg-eh-cream/60"
              >
                <td className="px-3 py-2.5">
                  {/* The keyboard path to the record; the row click is the pointer's. */}
                  <Link
                    to={href}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={IDS.learnerOpen(l.email)}
                    className="text-eh-ink hover:underline"
                  >
                    {l.name || l.email}
                  </Link>
                </td>
                <td className="px-3 py-2.5 text-eh-ink">
                  {l.completed_at ? t("path.dash.completed") : (l.current_step?.title || "—")}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={l.percent}
                      aria-label={t("path.dash.progressOf", { name: l.name || l.email })}
                      className="h-1.5 w-28 rounded-full bg-eh-cream overflow-hidden"
                    >
                      <div className="h-full bg-eh-sage-deep rounded-full" style={{ width: `${l.percent}%` }} />
                    </div>
                    <span className="text-xs text-eh-muted tabular-nums">{l.percent}%</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-eh-ink whitespace-nowrap">{fmtDaysAgo(l.last_activity_at, lang) || "—"}</td>
                <td className="px-3 py-2.5 text-eh-ink tabular-nums">{l.quiz_average == null ? "—" : `${l.quiz_average}%`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-eh-muted">{t("path.dash.rowHint")}</p>
    </div>
  );
}

function Chip({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={`rounded-full px-3 py-1 text-sm border ${
        active ? "border-eh-ochre-deep bg-eh-ochre/10 text-eh-ink font-semibold" : "border-eh-border-soft bg-white text-eh-muted hover:text-eh-ink"
      }`}
    >
      {children}
    </button>
  );
}

function BackLink() {
  const t = useT("admin");
  return (
    <Link to={adminHref(pathwayPaths.list)} className="inline-flex items-center gap-2 text-sm text-eh-muted hover:text-eh-ink">
      <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {t("path.builder.back")}
    </Link>
  );
}
